import type { GlossaryTerm } from "@/types/glossary";
import type { Storyboard } from "@/types/storyboard";

export const splitAliases = (aliases: string | null | undefined): string[] =>
  (aliases ?? "")
    .split(",")
    .map((alias) => alias.trim())
    .filter(Boolean);

// Structural/technical keys that never contain learner-visible text.
const SKIP_KEYS = new Set([
  "contentType",
  "subType",
  "contentElementId",
  "id",
  "position",
  "gapMark",
  "url",
]);

const collectStrings = (value: unknown, out: string[]): void => {
  if (typeof value === "string") {
    out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectStrings(item, out));
  } else if (value && typeof value === "object") {
    for (const [key, nested] of Object.entries(value)) {
      if (!SKIP_KEYS.has(key)) collectStrings(nested, out);
    }
  }
};

export const extractStoryboardText = (storyboards: Storyboard[]): string => {
  const out: string[] = [];
  collectStrings(storyboards, out);
  return out.join("\n").toLowerCase();
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Match at a word start but allow suffixes, so "Sprint" matches "Sprints"
// and "Sprintphase" (German compounds) but "KI" does not match "Wikipedia".
const matchesText = (candidate: string, lowerText: string): boolean =>
  new RegExp(
    `(?<![\\p{L}\\p{N}])${escapeRegExp(candidate.toLowerCase())}`,
    "u"
  ).test(lowerText);

export const getChapterTermIds = (
  terms: GlossaryTerm[],
  storyboards: Storyboard[]
): Set<string> => {
  const text = extractStoryboardText(storyboards);
  const ids = new Set<string>();
  if (!text) return ids;

  for (const term of terms) {
    const candidates = [term.name, ...splitAliases(term.aliases)].filter(
      (candidate) => candidate.trim().length >= 2
    );
    if (candidates.some((candidate) => matchesText(candidate, text))) {
      ids.add(term.id);
    }
  }
  return ids;
};
